import type { FormTemplate } from '../../types/schema';

export type FormsTemplateScope = 'inspection' | 'dailyService';

export type FormsTemplateDropdownOption = {
    id: string;
    name: string;
    kind: 'library' | 'company';
};

/** Prefer explicit type flags; fall back to “not daily service” for legacy rows. */
export function isDailyServiceFormTemplate(
    template: Pick<FormTemplate, 'dailyServiceLogForm' | 'inspectionForm'>,
): boolean {
    if (template.dailyServiceLogForm) return true;
    if (template.inspectionForm) return false;
    return false;
}

export function filterFormTemplatesForScope(
    formTemplates: FormTemplate[],
    scope: FormsTemplateScope,
): FormTemplate[] {
    return formTemplates.filter((template) => {
        const isDaily = isDailyServiceFormTemplate(template);
        return scope === 'dailyService' ? isDaily : !isDaily;
    });
}

/**
 * Library templates sometimes share the same display name across groups.
 * Keep the newest row per case-insensitive name so the menu is not noisy.
 */
export function dedupeFormTemplatesByName(formTemplates: FormTemplate[]): FormTemplate[] {
    const byName = new Map<string, FormTemplate>();
    for (const template of formTemplates) {
        const key = (template.templateName || '').trim().toLowerCase();
        if (!key) continue;
        const existing = byName.get(key);
        if (!existing) {
            byName.set(key, template);
            continue;
        }
        const existingUpdated = existing.updatedAt ? Date.parse(existing.updatedAt) : 0;
        const nextUpdated = template.updatedAt ? Date.parse(template.updatedAt) : 0;
        if (nextUpdated >= existingUpdated) {
            byName.set(key, template);
        }
    }
    return [...byName.values()].sort((a, b) =>
        (a.templateName || '').localeCompare(b.templateName || '', undefined, { sensitivity: 'base' }),
    );
}

export function buildFormsTemplateDropdownOptions(input: {
    formTemplates: FormTemplate[];
    scope: FormsTemplateScope;
    companyForms?: Array<{ id?: string | null; name?: string | null }>;
}): {
    libraryOptions: FormsTemplateDropdownOption[];
    companyOptions: FormsTemplateDropdownOption[];
    sectionLabel: string;
    companySectionLabel: string;
} {
    const scoped = filterFormTemplatesForScope(input.formTemplates, input.scope);
    const deduped = dedupeFormTemplatesByName(scoped);
    const libraryOptions = deduped.map((template) => ({
        id: template.id,
        name: template.templateName,
        kind: 'library' as const,
    }));

    const companySeen = new Set<string>();
    const companyOptions: FormsTemplateDropdownOption[] = [];
    for (const form of input.companyForms || []) {
        const id = form.id?.trim();
        const name = form.name?.trim();
        if (!id || !name) continue;
        const key = name.toLowerCase();
        if (companySeen.has(key)) continue;
        companySeen.add(key);
        companyOptions.push({ id, name, kind: 'company' });
    }
    companyOptions.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));

    return {
        libraryOptions,
        companyOptions,
        sectionLabel:
            input.scope === 'dailyService'
                ? 'Daily Service Log Templates'
                : 'Inspection Form Templates',
        companySectionLabel:
            input.scope === 'dailyService' ? 'Your Daily Service Forms' : 'Your Inspection Forms',
    };
}
